/**
 * Shared HTML5 drag payload for sidebar document tree rows (inline explorer + legacy tree).
 * Chat input and tab bar parse the same `application/json` shape.
 */
export type SidebarDocumentDragPayload = {
  id: string
  title: string
  path: string
  documentType: string
  nodeType: 'file' | 'folder'
}

export function inferDocumentTypeFromPath(path: string): string {
  if (/\.(png|jpe?g|gif|webp|svg)$/i.test(path)) return 'image'
  if (path.includes('work-spec') || path.includes('work_spec')) return 'work_spec'
  if (path.includes('voice-memo') || path.includes('voice_memo')) return 'voice_memo'
  if (path.includes('meeting')) return 'meeting'
  if (path.includes('idea')) return 'idea'
  if (path.includes('research')) return 'research'
  return 'note'
}

export function buildSidebarExplorerDragPayload(node: {
  id: string
  name: string
  path: string
  type: 'file' | 'folder'
}): SidebarDocumentDragPayload {
  return {
    id: node.id,
    title: node.name,
    path: node.path,
    documentType: node.type === 'file' ? inferDocumentTypeFromPath(node.path) : 'note',
    nodeType: node.type,
  }
}

/** Parse drag data from `dataTransfer.getData('application/json')`. Returns null if not our payload. */
export function parseSidebarDocumentDragJson(raw: string): SidebarDocumentDragPayload | null {
  if (!raw?.trim()) return null
  try {
    const data = JSON.parse(raw) as Record<string, unknown>
    const path = typeof data.path === 'string' ? data.path : undefined
    const id = typeof data.id === 'string' ? data.id : path
    if (!id) return null
    const resolvedPath = path ?? id
    const nodeType: 'file' | 'folder' =
      data.nodeType === 'folder' ? 'folder' : 'file'
    const title =
      typeof data.title === 'string'
        ? data.title
        : resolvedPath.split('/').pop() || id
    const documentType =
      typeof data.documentType === 'string'
        ? data.documentType
        : nodeType === 'file'
          ? inferDocumentTypeFromPath(resolvedPath)
          : 'note'
    return { id, title, path: resolvedPath, documentType, nodeType }
  } catch {
    return null
  }
}
